import { Offcanvas, Form, Card } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import ReactQuill from "react-quill-new";
import { useEffect, useState, useRef } from "react";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import CommonQuill from "../../components/editor/CommonQuill";

interface MediaFormData {
  show: boolean;
  handleClose: () => void;
  data?: any;
}

type FormData = {
  templateName: string;
  subject: string;
  code: string;
  cc: string; // 👈 comma separated
  possibleKeys: string; // comma separated keys
};

const AddEmailTemplate = ({ show, handleClose, data }: MediaFormData) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>();

  const [isLoading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const quillRef = useRef<any>(null);

  const [customErrors, setCustomErrors] = useState<{ message?: string }>({});

  const isQuillEmpty = (value?: string) => {
    if (!value) return true;

    const text = value
      .replace(/<(.|\n)*?>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();

    return text.length === 0;
  };

  const isEditMode = Boolean(data && data._id);

  useEffect(() => {
    if (show) {
      reset({
        templateName: data?.templateName || "",
        subject: data?.subject || "",
        code: data?.code || "",
        cc: data?.ccEmails?.join(", ") || "",
        possibleKeys: data?.possible_keys || "",
      });

      setMessage(data?.message || "");
    } else {
      reset({});
      setMessage("");
    }
  }, [show, data, reset]);

  const onSubmit = (form: FormData) => {
    if (!message || message.trim() === "<p><br></p>") {
      toast.error("Message is required");
      return;
    }

    if (isQuillEmpty(message)) {
      setCustomErrors({ message: "Message is required" });
      toast.error("Message is required");
      return;
    }

    // ✅ convert cc string -> array
    const ccEmails = form.cc
      ? form.cc
        .split(",")
        .map((e) => e.trim())
        .filter((e) => e !== "")
      : [];

    // ✅ convert possibleKeys string -> array

    const possible_keys = "";// form.possibleKeys
    //   ? form.possibleKeys
    //     .split(",")
    //     .map((k) => k.trim())
    //     .filter((k) => k !== "")
    //   : [];


    const payload = {
      templateName: form.templateName,
      subject: form.subject,
      code: form.code,
      message,
      ccEmails,
      possible_keys,
    };

    setLoading(true);

    const apiCall = isEditMode
      ? WebService.putAPI({
        action: `email-templates/edit/${data._id}`,
        body: payload,
      })
      : WebService.postAPI({
        action: `email-templates/add`,
        body: payload,
      });

    apiCall
      .then((res: any) => {
        toast.success(res.message || "Email template saved successfully!");
        reset();
        setMessage("");
        handleClose();
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Save failed");
      })
      .finally(() => setLoading(false));
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => {
        reset();
        setMessage("");
        handleClose();
      }}
      placement="end"
      className="offcanvas-custom"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          {isEditMode ? "Edit Email Template" : "Add Email Template"}
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <Form>
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Main Content
            </span>

            {/* ✅ TEMPLATE NAME */}
            <Form.Group className="mb-3">
              <Form.Label className="label">
                Template Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter template name"
                {...register("templateName", {
                  required: "Template name is required",
                })}
              />
              {errors.templateName && (
                <div className="error-message">{errors.templateName.message}</div>
              )}
            </Form.Group>

            {/* SUBJECT */}
            <Form.Group className="mb-3">
              <Form.Label className="label">
                Subject <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter subject"
                {...register("subject", { required: "Subject is required" })}
              />
              {errors.subject && (
                <div className="error-message">{errors.subject.message}</div>
              )}
            </Form.Group>

            {/* CODE */}
            <Form.Group className="mb-3">
              <Form.Label className="label">
                Code <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter code"
                {...register("code", { required: "Code is required" })}
                disabled={isEditMode}
              />
              {errors.code && (
                <div className="error-message">{errors.code.message}</div>
              )}
            </Form.Group>

            {/* CC EMAILS */}
            <Form.Group className="mb-3">
              <Form.Label className="label">CC Emails</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter emails separated by comma (example: a@gmail.com, b@gmail.com)"
                {...register("cc")}
              />
            </Form.Group>

            {/* POSSIBLE KEYS */}
            {/* <Form.Group className="mb-3">
              <Form.Label className="label">Possible Keys</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter keys separated by comma (example: {USERNAME}, {ORDER_ID})"
                {...register("possibleKeys")}
              />
              <small className="text-muted">
                Example: {"{USERNAME}"}, {"{ORDER_ID}"}, {"{PURCHASE_DATE}"}
              </small>
            </Form.Group> */}

            {/* MESSAGE */}
            <Form.Group>
              <Form.Label className="label">
                Message <span className="text-danger">*</span>
              </Form.Label>
              <CommonQuill
                ref={quillRef}
                value={message}
                onChange={setMessage}
                storage="EMAIL_TEMPLATES"
                initialHtml={show && data?.message ? data.message : undefined}
                onClearError={() =>
                  setCustomErrors((p) => ({
                    ...p,
                    message: undefined,
                  }))
                }
              />
            </Form.Group>
          </Card>
        </Form>
      </Offcanvas.Body>

      <div className="p-3">
        <CustomButton
          isLoading={isLoading}
          label="Save"
          className="w-100"
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </Offcanvas>
  );
};

export default AddEmailTemplate;
